// Helix·CX shell icons. Since UI migration pass 031 (the user's choice, 5 Oct
// 2026) they are lucide's, as the template's are: each name the shell and the
// nav use maps to the lucide icon its old hand-copied path came from
// (docs/design/handoff_owner_shell/prototype/shell/icons.jsx), at lucide's own
// stroke. Record: docs/testing/ui-migration/031-shell-icons.md.
import type { CSSProperties } from "react";
import {
  Activity,
  ArrowRight,
  Bell,
  Book,
  Bot,
  ChartLine,
  Check,
  ChevronRight,
  Clock,
  Cpu,
  CreditCard,
  House,
  LayoutGrid,
  ListOrdered,
  LogOut,
  Mail,
  Menu,
  MessageCircle,
  Moon,
  Package,
  PenTool,
  Phone,
  PhoneOutgoing,
  Plug,
  Route,
  Search,
  Settings,
  Shield,
  Sparkles,
  Sun,
  Ticket,
  Users,
  UsersRound,
  Workflow,
  Zap,
  type LucideIcon,
} from "lucide-react";

const ICONS = {
  home: House,
  engage: MessageCircle,
  tickets: Ticket,
  agents: Users,
  channels: Activity,
  design: PenTool,
  flow: Workflow,
  intel: ChartLine,
  customers: UsersRound,
  phone: Phone,
  settings: Settings,
  book: Book,
  box: Package,
  shield: Shield,
  route: Route,
  search: Search,
  menu: Menu,
  chevron: ChevronRight,
  arrow: ArrowRight,
  spark: Zap,
  sun: Sun,
  moon: Moon,
  grid: LayoutGrid,
  plug: Plug,
  cpu: Cpu,
  card: CreditCard,
  mail: Mail,
  check: Check,
  bell: Bell,
  logout: LogOut,
  // Named by the nav, missing from the old set (they drew the house).
  dialer: PhoneOutgoing,
  queue: ListOrdered,
  clock: Clock,
  bot: Bot,
  sparkle: Sparkles,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICONS;

export function ShellIcon({
  name,
  size = 17,
  d,
  style,
  className,
}: {
  name: string;
  size?: number;
  /** A raw 24px path, drawn instead of the named icon. */
  d?: string;
  style?: CSSProperties;
  className?: string;
}) {
  if (d) {
    return (
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={style}
        className={className}
      >
        <path d={d} />
      </svg>
    );
  }
  // An unknown name draws the house, as it always has.
  const Icon: LucideIcon = (ICONS as Record<string, LucideIcon>)[name] ?? House;
  return <Icon size={size} style={style} className={className} />;
}
